import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { useDataPhase, useWorkspaces } from '../data/store';
import { api } from '../data/api';
import type { Plan } from '../data/types';

interface Props {
  onClose: () => void;
  /** Prefill when converting a lead → client. */
  prefill?: { name?: string; category?: string; ownerEmail?: string };
  leadId?: string;
  /** Called instead of onClose after a successful convert. */
  onConverted?: () => void;
}

const slugify = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '').slice(0, 24);

export function NewClientModal({ onClose, prefill, leadId, onConverted }: Props) {
  const workspaces = useWorkspaces();
  const { reload } = useDataPhase();
  const [name, setName] = useState(prefill?.name ?? '');
  const [slug, setSlug] = useState(() => (prefill?.name ? slugify(prefill.name) : ''));
  const [category, setCategory] = useState(prefill?.category ?? '');
  const [plan, setPlan] = useState<Plan>('base');
  const [domain, setDomain] = useState('');
  const [workspace, setWorkspace] = useState(() => workspaces[0]?.id ?? 'risma');
  const [owner, setOwner] = useState('');
  const [email, setEmail] = useState(prefill?.ownerEmail ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !!(name.trim() && slug.trim() && email.trim()) && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await api.createClient({
        name: name.trim(),
        slug: slug.trim(),
        category: category.trim() || undefined,
        tier: plan,
        domain: domain.trim() || undefined,
        workspace,
        ownerName: owner.trim() || undefined,
        ownerEmail: email.trim(),
        ...(leadId ? { leadId } : {}),
      });
      reload();
      if (onConverted) onConverted();
      else onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(
        msg === 'slug_taken'
          ? 'Slug già in uso — scegline un altro.'
          : msg === 'invalid_request'
            ? 'Controlla i campi: nome, slug (solo minuscole e numeri) ed email valida.'
            : msg === 'forbidden'
              ? 'Non puoi creare clienti in questo spazio.'
              : `Errore: ${msg}`,
      );
      setBusy(false);
    }
  };

  // Escape closes; restore focus to the trigger on unmount.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);

  const onName = (v: string) => {
    setName(v);
    if (!slug || slug === slugify(name)) setSlug(slugify(v));
  };

  return (
    <div className="mscrim open" onMouseDown={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Nuovo cliente" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Nuovo cliente</h3>
          <button type="button" className="dr-close" onClick={onClose} aria-label="Chiudi">
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label htmlFor="nc-name">Nome attività</label>
            <input id="nc-name" className="input" value={name} placeholder="Trattoria Carlotta" autoFocus onChange={(e) => onName(e.target.value)} />
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="nc-slug">Slug</label>
              <input id="nc-slug" className="input" value={slug} placeholder="carlotta" onChange={(e) => setSlug(slugify(e.target.value))} />
            </div>
            <div className="field">
              <label htmlFor="nc-cat">Categoria</label>
              <input id="nc-cat" className="input" value={category} placeholder="Trattoria" onChange={(e) => setCategory(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label>Piano</label>
            <div className="rs-seg" style={{ alignSelf: 'flex-start' }}>
              <button type="button" className={plan === 'base' ? 'on' : ''} onClick={() => setPlan('base')}>Base</button>
              <button type="button" className={plan === 'completo' ? 'on' : ''} onClick={() => setPlan('completo')}>Completo</button>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="nc-domain">Dominio</label>
              <input id="nc-domain" className="input" value={domain} placeholder="trattoriacarlotta.it" onChange={(e) => setDomain(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="nc-ws">Spazio di lavoro</label>
              <select id="nc-ws" className="input" value={workspace} onChange={(e) => setWorkspace(e.target.value)}>
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="nc-owner">Titolare</label>
              <input id="nc-owner" className="input" value={owner} placeholder="Carlotta Conti" onChange={(e) => setOwner(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="nc-email">Email titolare</label>
              <input id="nc-email" className="input" type="email" value={email} placeholder="carlotta@trattoria.it" onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div className="rs-note">
            Crea il cliente e il suo titolare nel database e invia un magic-link di accesso al titolare. (Sito, DNS e
            dominio restano da configurare a parte.)
          </div>
          {error && (
            <div className="rs-error">{error}</div>
          )}
        </div>
        <div className="modal-foot">
          <div className="grow" />
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Annulla</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!canSubmit}>
            {busy ? 'Creo…' : 'Crea cliente'}
          </button>
        </div>
      </div>
    </div>
  );
}
